import { useState, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  HardDrive,
  Server,
  Database,
  FolderTree,
  Camera,
  Share2,
  Globe,
  Users,
  Network,
  Settings,
  Bell,
  Search,
  Menu,
  X,
  Moon,
  Sun,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard },
  { name: 'Storage', path: '/storage', icon: Database },
  { name: 'Physical Drives', path: '/drives', icon: HardDrive },
  { name: 'JBOD Enclosures', path: '/enclosures', icon: Server },
  { name: 'Storage Pools', path: '/pools', icon: Database },
  { name: 'Volumes & Datasets', path: '/volumes', icon: FolderTree },
  { name: 'Snapshots', path: '/snapshots', icon: Camera },
  { name: 'NAS Shares', path: '/shares', icon: Share2 },
  { name: 'SAN Targets', path: '/targets', icon: Globe },
  { name: 'Users & Groups', path: '/users', icon: Users },
  { name: 'Network', path: '/network', icon: Network },
  { name: 'System Settings', path: '/settings', icon: Settings },
];

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(true);
  const { user, logout, mockMode } = useAuth();

  const toggleTheme = () => {
    setDark(!dark);
    document.documentElement.classList.toggle('dark', !dark);
  };

  return (
    <div className={`min-h-screen flex ${dark ? 'dark' : ''}`}>
      {mockMode && (
        <div className="fixed top-0 inset-x-0 z-50 bg-amber-500 text-black text-xs font-semibold text-center py-1">
          MOCK STORAGE MODE ACTIVE — NO REAL STORAGE COMMANDS WILL EXECUTE
        </div>
      )}
      <aside
        className={`${collapsed ? 'w-16' : 'w-60'} hidden md:flex flex-col bg-slate-900 text-slate-200 transition-all duration-200 ${mockMode ? 'mt-5' : ''}`}
      >
        <div className="h-14 flex items-center px-4 border-b border-slate-800">
          {!collapsed && <span className="text-lg font-bold text-white tracking-tight">vStore</span>}
          <button onClick={() => setCollapsed(!collapsed)} className="ml-auto p-1 hover:bg-slate-800 rounded">
            <Menu size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-slate-800 transition-colors ${
                  isActive ? 'bg-slate-800 text-white border-r-2 border-indigo-500' : 'text-slate-400'
                }`
              }
            >
              <item.icon size={18} />
              {!collapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 px-3 py-2 w-full text-sm text-slate-400 hover:bg-slate-800 rounded"
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
            {!collapsed && <span>{dark ? 'Light mode' : 'Dark mode'}</span>}
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2 w-full text-sm text-red-400 hover:bg-slate-800 rounded mt-1"
          >
            <X size={18} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
        <header className={`h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center px-4 gap-4 ${mockMode ? 'mt-5' : ''}`}>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              System healthy
            </span>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div className="relative hidden sm:block">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search..."
                className="pl-9 pr-3 py-1.5 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button className="relative p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="hidden md:block text-sm">
                <div className="font-medium">{user?.displayName || user?.username}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 capitalize">{user?.role}</div>
              </div>
            </div>
          </div>
        </header>

        {mobileOpen && (
          <div className="md:hidden bg-slate-900 text-slate-200 p-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm rounded ${isActive ? 'bg-slate-800 text-white' : 'text-slate-400'}`
                }
              >
                <item.icon size={18} />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </div>
        )}

        <main className={`flex-1 overflow-auto p-4 md:p-6 ${mockMode ? 'mt-5' : ''}`}>{children}</main>
      </div>
    </div>
  );
}
